import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Button } from '@/shared/components/ui/Button';
import {
  CheckCircle2, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Clock3, X, Loader2, Plus, Trash2, Search
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { localToday } from '@/shared/utils/utils';

const INPUT = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100 transition-colors';
const LABEL = 'block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5';

type Direction = 'in' | 'out';

interface RejLine { productId: string; description: string; quantity: number; unitCost: number; }
interface Rejection {
  _id: string; direction: Direction; docNo: string; date: string;
  partyName: string; lines: RejLine[]; reason: string; status: 'draft' | 'posted' | 'cancelled';
}
interface Party { _id: string; displayName: string; legalName: string; }
interface Product { _id: string; name: string; sku: string; }

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    posted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  };
  return <span className={`uppercase text-[10px] px-2.5 py-0.5 rounded-full font-bold ${map[status] || map.draft}`}>{status}</span>;
}

function CreateRejectionModal({ direction, onClose, onCreated }: { direction: Direction; onClose: () => void; onCreated: () => void }) {
  const [partyId, setPartyId] = useState('');
  const [date, setDate] = useState(localToday());
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<{ productId: string; quantity: string; unitCost: string }[]>([{ productId: '', quantity: '', unitCost: '' }]);
  const [parties, setParties] = useState<Party[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get(`/api/accounting/parties?role=${direction === 'in' ? 'customer' : 'supplier'}`)
      .then((res) => setParties(res.data.data?.items || res.data.data || []))
      .catch(() => setParties([]));
    api.get('/api/accounting/items?limit=200')
      .then((res) => setProducts(res.data.data?.items || []))
      .catch(() => setProducts([]));
  }, [direction]);

  const setLine = (i: number, patch: Partial<{ productId: string; quantity: string; unitCost: string }>) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, { productId: '', quantity: '', unitCost: '' }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partyId) { setError(`Select a ${direction === 'in' ? 'customer' : 'supplier'}.`); return; }
    const cleanLines = lines
      .filter((l) => l.productId && Number(l.quantity) > 0)
      .map((l) => ({ productId: l.productId, quantity: Number(l.quantity), unitCost: Number(l.unitCost) || 0 }));
    if (cleanLines.length === 0) { setError('Add at least one item with a quantity.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post('/api/accounting/rejections', { direction, partyId, date, reason, lines: cleanLines });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Could not save this entry.');
    } finally {
      setSaving(false);
    }
  };

  const isIn = direction === 'in';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline">
          <div className={`flex items-center gap-2 ${isIn ? 'text-success-text' : 'text-danger-text'}`}>
            {isIn ? <ArrowDownLeft className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
            <h2 className="font-display text-[15px] font-semibold text-on-surface">Rejection {isIn ? 'In' : 'Out'}</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-on-surface-variant" /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-xs text-rose-700 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-lg p-2.5">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>{isIn ? 'Customer (who returned it)' : 'Supplier (who receives it back)'} *</label>
              <select required className={INPUT} value={partyId} onChange={(e) => setPartyId(e.target.value)}>
                <option value="">Select…</option>
                {parties.map((p) => <option key={p._id} value={p._id}>{p.displayName || p.legalName}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Date *</label>
              <input required type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Reason</label>
              <input className={INPUT} placeholder="e.g. Damaged in transit" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={LABEL + ' mb-0'}>Items</label>
              <button type="button" onClick={addLine} className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add line</button>
            </div>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className={INPUT + ' col-span-6'} value={line.productId} onChange={(e) => setLine(i, { productId: e.target.value })}>
                    <option value="">Select item…</option>
                    {products.map((p) => <option key={p._id} value={p._id}>{p.name} ({p.sku})</option>)}
                  </select>
                  <input type="number" min="0.001" step="0.001" placeholder="Qty" className={INPUT + ' col-span-3'} value={line.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                  <input type="number" min="0" step="0.01" placeholder="Unit cost" className={INPUT + ' col-span-2'} value={line.unitCost} onChange={(e) => setLine(i, { unitCost: e.target.value })} />
                  <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} className="col-span-1 p-2 text-slate-400 hover:text-rose-600 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-lg p-3 text-xs border ${isIn ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-900' : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-900 border-rose-100 dark:border-rose-900'}`}>
            Saved as a draft first — stock only moves once you Post it from the list.
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Draft
          </button>
        </div>
      </form>
    </div>
  );
}

export function RejectionsPage() {
  const [direction, setDirection] = useState<Direction>('in');
  const [items, setItems] = useState<Rejection[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/accounting/rejections?direction=${direction}&limit=200`);
      setItems(res.data.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [direction]);

  useEffect(() => { load(); }, [load]);

  const post = async (r: Rejection) => {
    try {
      await api.post(`/api/accounting/rejections/${r._id}/post`, {});
      showToast(`${r.docNo} posted — stock updated.`);
      load();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
  };
  const cancel = async (r: Rejection) => {
    try {
      await api.post(`/api/accounting/rejections/${r._id}/cancel`, {});
      showToast(`${r.docNo} cancelled.`);
      load();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
  };

  const q = search.toLowerCase();
  const filtered = items.filter((r) => !q || r.docNo.toLowerCase().includes(q) || r.partyName.toLowerCase().includes(q));

  const summary = useMemo(() => {
    const drafts = items.filter((r) => r.status === 'draft').length;
    const posted = items.filter((r) => r.status === 'posted').length;
    const inCount = items.filter((r) => r.direction === 'in').length;
    const outCount = items.filter((r) => r.direction === 'out').length;
    return {
      draftCount: drafts,
      postedCount: posted,
      inCount: inCount,
      outCount: outCount,
      total: items.length,
    };
  }, [items]);

  return (
    <div className="">
      <AccountingSubNav />
      <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
        {toast && (
          <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm shadow-sm">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" /> {toast}
          </div>
        )}
        <PageHeader
          icon={ArrowLeftRight}
          eyebrow="Inventory"
          title="Rejection In / Out"
          description="Goods rejected by a customer (In) or sent back to a supplier (Out). Posting moves real stock; no ledger entry is made."
          actions={<Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> New Entry</Button>}
        />

        {/* Replaces the old cards + bar chart. The list loads one direction at a
            time, so every figure is scoped to the direction being viewed. */}
        <StatGrid cols={3}>
          <StatCard label={direction === 'in' ? 'Customer returns (In)' : 'Vendor returns (Out)'} value={summary.total} hint="Entries in this view" icon={direction === 'in' ? ArrowDownLeft : ArrowUpRight} tone="primary" loading={loading} />
          <StatCard label="Drafts — stock pending" value={summary.draftCount} hint="Post to move inventory" icon={Clock3} tone="warning" loading={loading} />
          <StatCard label="Posted — stock updated" value={summary.postedCount} hint="Quantities reflected" icon={CheckCircle2} tone="success" loading={loading} />
        </StatGrid>

        <div role="tablist" aria-label="Return direction" className="flex gap-1 border-b border-outline">
          {(['in', 'out'] as Direction[]).map((d) => (
            <button
              key={d}
              role="tab"
              aria-selected={direction === d}
              onClick={() => setDirection(d)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                direction === d
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {d === 'in' ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
              Rejection {d === 'in' ? 'In (Customer Return)' : 'Out (Vendor Return)'}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
            placeholder="Search doc no, party…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-surface-3"
        >
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <ArrowLeftRight className="w-6 h-6" />
              </div>
              <p className="text-sm text-slate-400">No Rejection {direction === 'in' ? 'In' : 'Out'} entries yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>{['Doc No', 'Date', 'Party', 'Items', 'Reason', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((r) => (
                    <tr key={r._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-mono font-bold text-indigo-700 dark:text-indigo-400 text-xs">{r.docNo}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{r.partyName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{r.lines.length} item{r.lines.length !== 1 ? 's' : ''}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{r.reason || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {r.status === 'draft' && (
                          <>
                            <button onClick={() => post(r)} className="text-xs font-semibold text-emerald-600 hover:underline mr-3">Post</button>
                            <button onClick={() => cancel(r)} className="text-xs font-semibold text-slate-400 hover:underline">Cancel</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>

      {showCreate && (
        <CreateRejectionModal direction={direction} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); showToast('Draft saved.'); load(); }} />
      )}
    </div>
  );
}

export default RejectionsPage;
